// CoachPage — personal coach reflection journal. Visual preserved from
// original; content sourced from page_content row at slug `system-coach`.

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import {
  ColorBlock,
  HighlightList,
  type ColorBlockTint,
} from "@/components/page";

type ColorBlockSize = "sm" | "md" | "lg";

type HighlightItem = { marker: string; term: string; body: string };

type Block =
  | {
      type: "highlight-list";
      tint: ColorBlockTint;
      size?: ColorBlockSize;
      eyebrow?: string;
      lede?: string;
      defaultTone?: string;
      items: HighlightItem[];
    }
  | {
      type: "stat-card";
      tint: ColorBlockTint;
      size?: ColorBlockSize;
      eyebrow?: string;
      lede?: string;
      stat: string;
      footer?: string;
    }
  | {
      type: "quote";
      tint: ColorBlockTint;
      size?: ColorBlockSize;
      eyebrow?: string;
      quote: string;
    };

type CoachContent = {
  kind: "coach-v1";
  header: { eyebrow?: string; title: string; lede?: string };
  blocks: Block[];
};

const SLUG = "system-coach";

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-svh -mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50">
      <div className="mx-auto max-w-5xl space-y-6 sm:space-y-7">{children}</div>
    </div>
  );
}

function renderBlock(block: Block, idx: number): React.ReactNode {
  if (block.type === "highlight-list") {
    return (
      <ColorBlock
        key={idx}
        tint={block.tint}
        size={block.size ?? "lg"}
        eyebrow={block.eyebrow}
      >
        {block.lede && (
          <p className="text-base text-stone-700/85 leading-relaxed mb-4 max-w-2xl">
            {block.lede}
          </p>
        )}
        <HighlightList
          defaultTone={(block.defaultTone ?? "emerald") as never}
          items={block.items as never}
        />
      </ColorBlock>
    );
  }
  if (block.type === "stat-card") {
    return (
      <ColorBlock
        key={idx}
        tint={block.tint}
        size={block.size ?? "lg"}
        eyebrow={block.eyebrow}
      >
        {block.lede && (
          <p className="text-base text-stone-700/85 leading-relaxed mb-3 max-w-2xl">
            {block.lede}
          </p>
        )}
        <p className="text-2xl sm:text-3xl font-bold tracking-tight text-stone-900 mb-3">
          {block.stat}
        </p>
        {block.footer && (
          <p className="text-sm text-stone-700/85 leading-relaxed max-w-md">
            {block.footer}
          </p>
        )}
      </ColorBlock>
    );
  }
  if (block.type === "quote") {
    return (
      <ColorBlock
        key={idx}
        tint={block.tint}
        size={block.size ?? "md"}
        eyebrow={block.eyebrow}
      >
        <p className="text-2xl sm:text-3xl font-medium leading-snug tracking-tight text-stone-900 max-w-2xl">
          {block.quote}
        </p>
      </ColorBlock>
    );
  }
  return null;
}

export function CoachPage() {
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", SLUG],
    queryFn: async (): Promise<CoachContent> => {
      const res = await api(`/api/kb/${SLUG}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { content: CoachContent };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex items-center gap-2 p-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          טוען…
        </div>
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame>
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את העמוד</div>
          {error && (
            <div className="mt-2 text-xs text-rose-600/80">{(error as Error).message}</div>
          )}
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      <header>
        {data.header.eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            {data.header.eyebrow}
          </p>
        )}
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-stone-900">
          {data.header.title}
        </h1>
        {data.header.lede && (
          <p className="mt-3 max-w-2xl text-base sm:text-lg text-stone-600 leading-snug">
            {data.header.lede}
          </p>
        )}
      </header>
      {data.blocks.map(renderBlock)}
    </PageFrame>
  );
}
